import type { ReactNode } from 'react'
import type { CommentKind } from '../review/contracts'

// Kind palette per spec §8.2 (pill bg / pill accent), verdict colors per §8.4.

export type { CommentKind }

const KIND_META: Record<
  CommentKind,
  { label: string; bg: string; fg: string }
> = {
  praise: {
    label: 'Praise',
    bg: 'color-mix(in oklab, var(--cd-green) 14%, transparent)',
    fg: 'var(--cd-green)',
  },
  critique: {
    label: 'Critique',
    bg: 'color-mix(in oklab, var(--cd-red) 14%, transparent)',
    fg: 'var(--cd-red)',
  },
  suggestion: {
    label: 'Suggestion',
    bg: 'color-mix(in oklab, var(--cd-purple) 14%, transparent)',
    fg: 'var(--cd-purple)',
  },
}

export function KindPill(props: {
  kind: CommentKind
  ai?: boolean
}): ReactNode {
  const { kind, ai } = props
  const meta = KIND_META[kind]
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: meta.bg, color: meta.fg }}
    >
      {ai ? `✦ ${meta.label}` : meta.label}
    </span>
  )
}

export type Verdict =
  | 'faster'
  | 'in-range'
  | 'over-range'
  | 'far-over'
  | 'on-target'
  | 'over-target'

const VERDICT_META: Record<Verdict, { label: string; color: string }> = {
  faster: { label: 'Faster', color: 'var(--cd-green)' },
  'in-range': { label: 'In range', color: 'var(--cd-green-soft)' },
  'over-range': { label: 'Over range', color: 'var(--cd-amber)' },
  'far-over': { label: 'Far over', color: 'var(--cd-red)' },
  'on-target': { label: 'On target', color: 'var(--cd-green)' },
  'over-target': { label: 'Over target', color: 'var(--cd-amber)' },
}

/**
 * Ceiling-based verdict thresholds (spec §8.5): `> 2x target` -> Far over
 * (red); `> target` -> Over target (amber); else On target (green).
 * Boundaries: value === target -> On target; value === 2x target -> Over
 * target (not yet "far"); value > 2x target -> Far over.
 *
 * One rule, one home: behavioral's Delivery tiles and coding's Verification
 * counts (LG-637) both judge a count against a coach-set ceiling, and two
 * copies of the same thresholds would let the two tabs drift to different
 * math for the same pill. Lives beside `Verdict` because it is the rule that
 * picks from that vocabulary.
 */
export function ceilingVerdict(value: number, target: number): Verdict {
  if (value > target * 2) return 'far-over'
  if (value > target) return 'over-target'
  return 'on-target'
}

export function VerdictPill(props: {
  verdict: Verdict
  label?: string
}): ReactNode {
  const { verdict, label } = props
  const meta = VERDICT_META[verdict]
  return (
    <span
      className="inline-flex items-center rounded-full bg-(--cd-inset) px-2 py-0.5 text-[11px] font-semibold"
      style={{ color: meta.color }}
    >
      {label ?? meta.label}
    </span>
  )
}

export type TrackType = 'coding' | 'sd' | 'behavioral' | 'interview' | 'other'

const TYPE_COLOR: Record<TrackType, string> = {
  coding: 'var(--cd-amber)',
  sd: 'var(--cd-purple)',
  behavioral: 'var(--cd-blue)',
  interview: 'var(--cd-green)',
  other: 'var(--cd-gray)',
}

export function TypeDot(props: { type: TrackType }): ReactNode {
  const { type } = props
  return (
    <span
      aria-hidden="true"
      className="inline-block size-2 shrink-0 rounded-full"
      style={{ background: TYPE_COLOR[type] }}
    />
  )
}

const STATUS_TONE: Record<'green' | 'red' | 'amber' | 'neutral', string> = {
  green: 'var(--cd-green)',
  red: 'var(--cd-red)',
  amber: 'var(--cd-amber)',
  neutral: 'var(--cd-text-muted)',
}

export function StatusPill(props: {
  tone: 'green' | 'red' | 'amber' | 'neutral'
  children: ReactNode
}): ReactNode {
  const { tone, children } = props
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-(--cd-inset) px-2 py-0.5 text-[11px] font-semibold"
      style={{ color: STATUS_TONE[tone] }}
    >
      {children}
    </span>
  )
}

const COMPANY_META: Record<string, { letter: string; color: string }> = {
  google: { letter: 'G', color: '#4285F4' },
  amazon: { letter: 'A', color: '#c98a10' },
  meta: { letter: 'M', color: '#0866FF' },
  netflix: { letter: 'N', color: '#E50914' },
}

export function CompanyIcon(props: {
  company: string
  size?: number
}): ReactNode {
  const { company, size = 20 } = props
  const meta = COMPANY_META[company.toLowerCase()]
  const letter = meta?.letter ?? (company.charAt(0).toUpperCase() || '?')
  const color = meta?.color ?? 'var(--cd-gray)'

  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
      style={{ width: size, height: size, background: color }}
    >
      {letter}
    </span>
  )
}
